import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Download,
  Calendar,
  DollarSign,
  User,
  ChevronRight,
  Layers,
  List,
  ArrowUpDown,
  RefreshCw,
  Eye
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getAllPayslips, downloadPayslipPDF } from '../../api/payroll';
import BlurredValue from '../../components/common/BlurredValue';
import PayslipDetailModal from '../../components/payroll/PayslipDetailModal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PeriodFilter from '../../components/common/PeriodFilter';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';

const PayslipsPage = () => {
  const navigate = useNavigate();
  const { periodRange, getDateRangeParams } = useFiscalPeriod();
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayslipId, setSelectedPayslipId] = useState(null);

  // Filters and sorting
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'group'
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' (newest first) or 'asc' (oldest first)

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const fetchPayslips = useCallback(async () => {
    try {
      setLoading(true);
      const { startDate, endDate } = getDateRangeParams();
      const data = await getAllPayslips({ module: 'retail', limit: 500, startDate, endDate });
      setPayslips(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching payslips:', error);
      toast.error('Failed to load payslips');
      setPayslips([]);
    } finally {
      setLoading(false);
    }
  }, [getDateRangeParams]);

  useEffect(() => {
    fetchPayslips();
  }, [fetchPayslips, periodRange]);

  const handleDownloadPDF = async (e, payslipId, payslipNumber) => {
    e.stopPropagation();
    try {
      toast.info('Generating PDF...');
      await downloadPayslipPDF(payslipId, payslipNumber);
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Sorting logic
  const sortedPayslips = [...payslips].sort((a, b) => {
    const dateA = new Date(a.pay_period_end || 0);
    const dateB = new Date(b.pay_period_end || 0);
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  // Grouping logic (by payroll run)
  const groupedPayslips = sortedPayslips.reduce((groups, payslip) => {
    const runId = payslip.payroll_run_id || 'unknown';
    const runNum = payslip.run_number || 'Unknown Run';
    const runDesc = payslip.run_description || '';
    if (!groups[runId]) {
      groups[runId] = {
        runId,
        runNumber: runNum,
        description: runDesc,
        payslips: []
      };
    }
    groups[runId].payslips.push(payslip);
    return groups;
  }, {});

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-800',
      unpaid: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-blue-100 text-blue-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-[1700px] mx-auto pb-10 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-primary-black flex items-center gap-2">
              <FileText className="w-6 h-6 text-accent-gold" />
              Employee Payslips
            </h1>
            <p className="text-sm text-gray-500">View and download payslips across all employees</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <PeriodFilter storageKey="payslips" />
            <button
              onClick={fetchPayslips}
              className="p-2 border rounded-lg hover:bg-gray-50 bg-white transition w-fit"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Sorting */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-400 uppercase">Sort</span>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="border rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-accent-gold"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>

          {/* View Toggles */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1 w-fit">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition ${viewMode === 'list' ? 'bg-white text-primary-black shadow-sm' : 'text-gray-500 hover:text-primary-black'}`}
            >
              <List size={14} />
              List View
            </button>
            <button
              onClick={() => setViewMode('group')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition ${viewMode === 'group' ? 'bg-white text-primary-black shadow-sm' : 'text-gray-500 hover:text-primary-black'}`}
            >
              <Layers size={14} />
              Group by Run
            </button>
          </div>
        </div>

        {/* Content Area */}
        {sortedPayslips.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-16 text-center">
            <FileText className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-primary-black">No Payslips Found</h3>
            <p className="text-gray-400 text-sm mt-1">
              No payslips match the selected period or filters.
            </p>
          </div>
        ) : viewMode === 'list' ? (
          /* List View */
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase">
                    <th className="px-6 py-3">Employee</th>
                    <th className="px-6 py-3">Branch</th>
                    <th className="px-6 py-3">Period</th>
                    <th className="px-6 py-3 text-right">Gross Pay</th>
                    <th className="px-6 py-3 text-right">Deductions</th>
                    <th className="px-6 py-3 text-right">Net Pay</th>
                    <th className="px-6 py-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedPayslips.map((payslip) => (
                    <tr
                      key={payslip.id}
                      className="hover:bg-gray-50 transition cursor-pointer"
                      onClick={() => setSelectedPayslipId(payslip.id)}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-semibold text-primary-black text-sm">
                            {payslip.full_name}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">{payslip.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {payslip.branch_name || 'All'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDate(payslip.pay_period_start)} -{' '}
                        {formatDate(payslip.pay_period_end)}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-primary-black">
                        <BlurredValue value={formatCurrency(payslip.gross_salary)} />
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-red-600">
                        <BlurredValue value={formatCurrency(payslip.total_deductions)} />
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-green-600">
                        <BlurredValue value={formatCurrency(payslip.net_salary)} />
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium uppercase ${getStatusBadge(payslip.payment_status)}`}
                        >
                          {payslip.payment_status || 'unpaid'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Grouped by Payroll Run View */
          <div className="space-y-6">
            {Object.values(groupedPayslips).map((group) => (
              <div
                key={group.runId}
                className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
              >
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-primary-black text-base flex items-center gap-2">
                      <Layers className="w-5 h-5 text-gray-400" />
                      {group.description || `Payroll Run #${group.runNumber}`}
                    </h3>
                    <p className="text-xs text-gray-400 mt-1">
                      Run Number: <span className="font-mono font-medium">{group.runNumber}</span>
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-accent-gold">
                    {group.payslips.length} payslip(s)
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-50/50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase">
                        <th className="px-6 py-3">Employee</th>
                        <th className="px-6 py-3">Period</th>
                        <th className="px-6 py-3 text-right">Gross Pay</th>
                        <th className="px-6 py-3 text-right">Deductions</th>
                        <th className="px-6 py-3 text-right">Net Pay</th>
                        <th className="px-6 py-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {group.payslips.map((payslip) => (
                        <tr
                          key={payslip.id}
                          className="hover:bg-gray-50 transition cursor-pointer"
                          onClick={() => setSelectedPayslipId(payslip.id)}
                        >
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-semibold text-primary-black text-sm">
                                {payslip.full_name}
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">{payslip.email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {formatDate(payslip.pay_period_start)} -{' '}
                            {formatDate(payslip.pay_period_end)}
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-primary-black">
                            <BlurredValue value={formatCurrency(payslip.gross_salary)} />
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-red-600">
                            <BlurredValue value={formatCurrency(payslip.total_deductions)} />
                          </td>
                          <td className="px-6 py-4 text-right text-sm font-semibold text-green-600">
                            <BlurredValue value={formatCurrency(payslip.net_salary)} />
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium uppercase ${getStatusBadge(payslip.payment_status)}`}
                            >
                              {payslip.payment_status || 'unpaid'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedPayslipId && (
        <PayslipDetailModal
          payslipId={selectedPayslipId}
          isOpen={true}
          onClose={() => setSelectedPayslipId(null)}
          module="retail"
        />
      )}
    </div>
  );
};

export default PayslipsPage;
